import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import InstallAppPrompt from "./components/InstallAppPrompt";
import OfflineQueueBadge from "./components/OfflineQueueBadge";
import { bootstrapOfflineQueue } from "./utils/offlineQueue";
import "@/App.css";
import { useState, useEffect, lazy, Suspense } from "react";
import LandingPage from "./pages/LandingPage";
import RegisterPage from "./pages/RegisterPage";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import LojaShell from "./layouts/LojaShell";
import BackgroundCheckBanner from "./components/BackgroundCheckBanner";
import { TermosPage, PrivacidadePage, CookiesPage } from "./pages/LegalPages";
import InvitePage from "./pages/InvitePage";
import ForcePasswordChangeModal from "./components/ForcePasswordChangeModal";
import FranchiseeShell from "./layouts/FranchiseeShell";
import LiveStatsWidget from "./components/LiveStatsWidget";
import { useAnalytics } from "./hooks/useAnalytics";
import { useWebSocket, requestNotificationPermission } from "./hooks/useWebSocket";
import { useWakeLock } from "./hooks/useWakeLock";
import ModuleLayout from "./layouts/ModuleLayout";
import DriverShell from "./layouts/DriverShell";
import ClienteShell from "./layouts/ClienteShell";
import { CartProvider } from "./contexts/CartContext";
import { moduleConfigs, getModuleForUser, getModuleHomeRoute } from "./layouts/moduleConfigs";
import PWAInstallPrompt from "./components/PWAInstallPrompt";

// Lazy-loaded page modules (code-splitting for faster initial paint)
const UserDashboard = lazy(() => import("./pages/UserDashboard"));
const MerchantDashboard = lazy(() => import("./pages/MerchantDashboard"));
const LojaDashboardPage = lazy(() => import("./pages/LojaDashboardPage"));
const LojaMinhaLojaPage = lazy(() => import("./pages/LojaMinhaLojaPage"));
const LojaKitchenPage = lazy(() => import("./pages/LojaKitchenPage"));
const LojaOrdersPage = lazy(() => import("./pages/LojaOrdersPage"));
const LojaDevolucoesPage = lazy(() => import("./pages/LojaDevolucoesPage"));
const LojaAnalyticsPage = lazy(() => import("./pages/LojaAnalyticsPage"));
const LojaMapPage = lazy(() => import("./pages/LojaMapPage"));
const LojaProductsPage = lazy(() => import("./pages/LojaProductsPage"));
const LojaChatsPage = lazy(() => import("./pages/LojaChatsPage"));
const LojaComingSoonPage = lazy(() => import("./pages/LojaComingSoonPage"));
const LojaSettingsPage = lazy(() => import("./pages/LojaSettingsPage"));
const NossosGruposPage = lazy(() => import("./pages/NossosGruposPage"));
const MigrateToDriverPage = lazy(() => import("./pages/MigrateToDriverPage"));
const AdminGruposPage = lazy(() => import("./pages/AdminGruposPage"));
const DriverDashboard = lazy(() => import("./pages/DriverDashboard"));
const DriverDashboardPremium = lazy(() => import("./pages/DriverDashboardPremium"));
const DriverProfilePremium = lazy(() => import("./pages/DriverProfilePremium"));
const DriverOrdersPage = lazy(() => import("./pages/DriverOrdersPage"));
const DriverRegistrationPage = lazy(() => import("./pages/DriverRegistrationPage"));
const NetworkPage = lazy(() => import("./pages/NetworkPage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const LeaderboardPage = lazy(() => import("./pages/LeaderboardPage"));
const CommissionsPage = lazy(() => import("./pages/CommissionsPage"));
const OrderDetailsPage = lazy(() => import("./pages/OrderDetailsPage"));
const MyOrdersPage = lazy(() => import("./pages/MyOrdersPage"));
const HelpPage = lazy(() => import("./pages/HelpPage"));
const ChatsPage = lazy(() => import("./pages/ChatsPage"));
const PreLaunchPage = lazy(() => import("./pages/PreLaunchPage"));
const WalletPage = lazy(() => import("./pages/WalletPage"));
const AdminWithdrawalsPage = lazy(() => import("./pages/AdminWithdrawalsPage"));
const OrderTrackingPage = lazy(() => import("./pages/OrderTrackingPage"));
const DriverMapPage = lazy(() => import("./pages/DriverMapPage"));
const MerchantBillingPage = lazy(() => import("./pages/MerchantBillingPage"));
const AdminMerchantBillingPage = lazy(() => import("./pages/AdminMerchantBillingPage"));
const AdminFinancialDashboard = lazy(() => import("./pages/AdminFinancialDashboard"));
const AdminPaymentsAuditPage = lazy(() => import("./pages/AdminPaymentsAuditPage"));
const ClienteAddressesPage = lazy(() => import("./pages/ClienteAddressesPage"));
const ClienteNotificationsPage = lazy(() => import("./pages/ClienteNotificationsPage"));
const ClienteFavoritesPage = lazy(() => import("./pages/ClienteFavoritesPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const AdminCouponsPage = lazy(() => import("./pages/AdminCouponsPage"));
const AdminEmailsPage = lazy(() => import("./pages/AdminEmailsPage"));
const AdminWhatsAppPage = lazy(() => import("./pages/AdminWhatsAppPage"));
const AdminKycPage = lazy(() => import("./pages/AdminKycPage"));
const VerificacaoKycPage = lazy(() => import("./pages/VerificacaoKycPage"));
const AdminReturnsDashboardPage = lazy(() => import("./pages/AdminReturnsDashboardPage"));
const AdminMobilePage = lazy(() => import("./pages/AdminMobilePage"));
const AdminPublicidadePage = lazy(() => import("./pages/AdminPublicidadePage"));
const AdminChatPage = lazy(() => import("./pages/AdminChatPage"));
const AdvertiserLoginPage = lazy(() => import("./pages/AdvertiserLoginPage"));
const AdvertiserSetupPasswordPage = lazy(() => import("./pages/AdvertiserSetupPasswordPage"));
const AdvertiserDashboardPage = lazy(() => import("./pages/AdvertiserDashboardPage"));
const AdvertiseLandingPage = lazy(() => import("./pages/AdvertiseLandingPage"));
const RecruitmentLandingPage = lazy(() => import("./pages/RecruitmentLandingPage"));
const AdminRecruitmentPage = lazy(() => import("./pages/AdminRecruitmentPage"));
const AdminCnhValidationsPage = lazy(() => import("./pages/AdminCnhValidationsPage"));
const BolaoPage = lazy(() => import("./pages/BolaoPage"));
const AdminBolaoPage = lazy(() => import("./pages/AdminBolaoPage"));
const AdminSociosPage = lazy(() => import("./pages/AdminSociosPage"));
const AdminFaceVerificationsPage = lazy(() => import("./pages/AdminFaceVerificationsPage"));
const AdminIdentityReviewPage = lazy(() => import("./pages/AdminIdentityReviewPage"));
const SocioOnboardingPage = lazy(() => import("./pages/SocioOnboardingPage"));
const SocioDashboardPage = lazy(() => import("./pages/SocioDashboardPage"));
const OnboardingPage = lazy(() => import("./pages/OnboardingPage"));
import UserChatWidget from "./components/UserChatWidget";
const AdminSettingsPage = lazy(() => import("./pages/AdminSettingsPage"));
const AdminVouchersPage = lazy(() => import("./pages/AdminVouchersPage"));
const AdminFranchisesPage = lazy(() => import("./pages/AdminFranchisesPage"));
const FranchiseeDashboard = lazy(() => import("./pages/FranchiseeDashboard"));
const FranchiseeDashboardV2 = lazy(() => import("./pages/FranchiseeDashboardV2"));
const FranchiseeOrdersCenter = lazy(() => import("./pages/FranchiseeOrdersCenter"));
const FranchiseeDriversCenter = lazy(() => import("./pages/FranchiseeDriversCenter"));
const FranchiseeCustomersCenter = lazy(() => import("./pages/FranchiseeCustomersCenter"));
const FranchiseeChatPage = lazy(() => import("./pages/FranchiseeChatPage"));
const FranchiseeBroadcastsPage = lazy(() => import("./pages/FranchiseeBroadcastsPage"));
const FranchiseeStoresPage = lazy(() => import("./pages/FranchiseeStoresPage"));
const FranchiseeFinancePage = lazy(() => import("./pages/FranchiseeFinancePage"));
const FranchiseeCitySettingsPage = lazy(() => import("./pages/FranchiseeCitySettingsPage"));
const FranchiseeProfilePage = lazy(() => import("./pages/FranchiseeProfilePage"));
const FranchiseeAnalyticsPage = lazy(() => import("./pages/FranchiseeAnalyticsPage"));
const FranchiseeRankingsPage = lazy(() => import("./pages/FranchiseeRankingsPage"));
const FranchiseeReportsPage = lazy(() => import("./pages/FranchiseeReportsPage"));
const MerchantChatPage = lazy(() => import("./pages/MerchantChatPage"));
const AdminAnalyticsPage = lazy(() => import("./pages/AdminAnalyticsPage"));
const AdminCitiesPage = lazy(() => import("./pages/AdminCitiesPage"));
const AdminUsersPage = lazy(() => import("./pages/AdminUsersPage"));
const AdminDriversPage = lazy(() => import("./pages/AdminDriversPage"));
const AdminMerchantsPage = lazy(() => import("./pages/AdminMerchantsPage"));
const AdminNetworkPage = lazy(() => import("./pages/AdminNetworkPage"));
const AdminSupportPage = lazy(() => import("./pages/AdminSupportPage"));
const AdminNichesPage = lazy(() => import("./pages/AdminNichesPage"));
const AdminPlatformRatesPage = lazy(() => import("./pages/AdminPlatformRatesPage"));
const AdminIntegrationsPage = lazy(() => import("./pages/AdminIntegrationsPage"));
const AdminIntegrationsDynamicPage = lazy(() => import("./pages/AdminIntegrationsDynamicPage"));
const AdminBannersPage = lazy(() => import("./pages/AdminBannersPage"));
const AdminFiscalPage = lazy(() => import("./pages/AdminFiscalPage"));
const AdminSOSPage = lazy(() => import("./pages/AdminSOSPage"));
const BackgroundCheckPage = lazy(() => import("./pages/BackgroundCheckPage"));
const AdminBackgroundCheckPage = lazy(() => import("./pages/AdminBackgroundCheckPage"));
const AdminCampaignsPage = lazy(() => import("./pages/AdminCampaignsPage"));
const AdminHierarchyPage = lazy(() => import("./pages/AdminHierarchyPage"));
const MasterFranchiseDashboard = lazy(() => import("./pages/MasterFranchiseDashboard"));
const FranchiseAdminDashboard = lazy(() => import("./pages/FranchiseAdminDashboard"));
const SupportPage = lazy(() => import("./pages/SupportPage"));
const ProChatPage = lazy(() => import("./pages/ProChatPage"));
const AdminShopPage = lazy(() => import("./pages/AdminShopPage"));
const AdminSafetyCenter = lazy(() => import("./pages/AdminSafetyCenter"));
const DriverShopPage = lazy(() => import("./pages/DriverShopPage"));
const ClienteHomePage = lazy(() => import("./pages/ClienteHomePage"));
const ClienteSearchPage = lazy(() => import("./pages/ClienteSearchPage"));
const ClienteRestaurantPage = lazy(() => import("./pages/ClienteRestaurantPage"));
const ClienteCartPage = lazy(() => import("./pages/ClienteCartPage"));
const ClienteCheckoutPage = lazy(() => import("./pages/ClienteCheckoutPage"));
const ClienteProfilePage = lazy(() => import("./pages/ClienteProfilePage"));
const ClienteTrackingPage = lazy(() => import("./pages/ClienteTrackingPage"));
const ClienteRewardsPage = lazy(() => import("./pages/ClienteRewardsPage"));
const AuditLogsPage = lazy(() => import("./pages/AuditLogsPage"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

/**
 * Stable wrapper components — defined OUTSIDE `App` so their function identity
 * never changes across re-renders. Defining them inside App caused React to
 * unmount + remount the entire route subtree on every WS message / state
 * update, blowing away in-page state (orders list, filters, scroll, etc).
 */
const ProtectedRoute = ({ token, children }) => {
  if (!token) return <Navigate to="/login" />;
  return children;
};

const WithModule = ({ moduleKey, token, user, logout, children }) => {
  // useLocation garante re-render em navegação client-side (window.location não é reativo)
  const location = useLocation();
  if (!token) return <Navigate to="/login" />;
  if (!user) return null;
  // Driver module renders its own premium dark shell with header + bottom nav.
  // The home page (DriverDashboard) renders DriverShell itself (with live
  // earnings/available counts); other tabs are wrapped here so the shell is
  // consistent across all driver routes.
  if (moduleKey === "entregador") {
    if (location.pathname === "/entregador/entregas") {
      // Home do driver tem seu próprio header — mas precisa do banner de bg_check
      // (DriverShell é bypassado nessa rota para não duplicar UI premium).
      return (
        <>
          <BackgroundCheckBanner user={user} />
          {children}
        </>
      );
    }
    return (
      <DriverShell user={user} logout={logout}>
        {children}
      </DriverShell>
    );
  }
  // Cliente module uses the premium glass shell (bottom nav only, no sidebar).
  if (moduleKey === "cliente") {
    return <ClienteShell>{children}</ClienteShell>;
  }
  // Loja module: premium dark operational shell.
  if (moduleKey === "loja") {
    return (
      <LojaShell user={user} logout={logout}>
        {children}
      </LojaShell>
    );
  }
  const config = moduleConfigs[moduleKey];
  if (!config) return children;
  return (
    <ModuleLayout config={config} user={user} logout={logout}>
      {children}
    </ModuleLayout>
  );
};

const AdminOnly = ({ token, user, children }) => {
  if (!token) return <Navigate to="/login" />;
  if (!user) return null;
  if (user?.user_type !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

const SmartRedirect = ({ token, user }) => {
  if (!token) return <Navigate to="/login" />;
  if (!user) return null;
  const moduleKey = getModuleForUser(user);
  return <Navigate to={getModuleHomeRoute(moduleKey)} replace />;
};

// Analytics + live widget — must be inside <BrowserRouter> to access useLocation
function AnalyticsBoot({ user }) {
  useAnalytics();
  // Hide floating widget for logged-in operational users (driver/merchant/admin)
  // to avoid noise. Keep for unauthenticated visitors and clients.
  const showWidget = !user || user.user_type === "user";
  return showWidget ? <LiveStatsWidget /> : null;
}

function App() {
  // Hydrate user from localStorage immediately to avoid login flash on every reload
  const [user, setUser] = useState(() => {
    try {
      const cached = localStorage.getItem("user");
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });
  const [token, setToken] = useState(localStorage.getItem("token"));

  // Mantém a tela do dispositivo sempre acesa enquanto a aba estiver visível
  // (Screen Wake Lock API — evita que celular apague por inatividade).
  useWakeLock(true);

  useEffect(() => {
    if (token) {
      fetchUser();
    }
  }, [token]);

  // Connect to global WebSocket for real-time notifications across all pages
  useWebSocket(token, {
    showToasts: true,
    enableSound: true,
    onConnect: () => console.log("[WS] connected"),
    onDisconnect: () => console.log("[WS] disconnected"),
    onMessage: (data) => {
      // Re-broadcast the message as a window event so any component
      // (NotificationBell, dashboards, chat) can react and refetch.
      try {
        window.dispatchEvent(new CustomEvent("gofood:ws", { detail: data }));
        if (data?.type) {
          window.dispatchEvent(
            new CustomEvent(`gofood:ws:${data.type}`, { detail: data })
          );
        }
      } catch {
        // ignore
      }
    },
  });

  // Register service worker for Web Push and request notification permission once
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .register("/sw.js")
        .catch((err) => console.warn("SW registration failed:", err));
    }
    // Inicializa a fila offline (drena pendentes ao voltar online)
    bootstrapOfflineQueue();
    if (token) {
      requestNotificationPermission();
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        // Cache user so we can hydrate on cold start without flashing login screen
        try { localStorage.setItem("user", JSON.stringify(userData)); } catch { /* quota */ }
      } else if (response.status === 401) {
        // Token actually invalid/expired — only then drop the session
        logout();
      }
      // Any other status (5xx, network blip): keep the cached session,
      // user stays logged in. Next request will re-validate.
    } catch (error) {
      // Network error → don't kick the user out, just log
      console.warn("fetchUser network error (keeping session):", error?.message);
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setToken(null);
    setUser(null);
  };

  /** Stable wrapper components are defined module-scope above. */

  return (
    <div className="App">
      <BrowserRouter>
        <CartProvider>
        <PWAInstallPrompt />
        <AnalyticsBoot user={user} />
        {token && user && user.user_type !== "admin" && user.user_type !== "advertiser" && (
          <UserChatWidget user={user} />
        )}
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center bg-white" data-testid="route-loading">
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 rounded-full border-4 border-orange-200 border-t-[#FF6B00] animate-spin" />
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Carregando…</span>
            </div>
          </div>
        }>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route
            path="/register/:referralCode"
            element={<RegisterPage setToken={setToken} setUser={setUser} />}
          />
          <Route
            path="/register"
            element={<RegisterPage setToken={setToken} setUser={setUser} />}
          />
          <Route path="/termos" element={<TermosPage />} />
          <Route path="/privacidade" element={<PrivacidadePage />} />
          <Route path="/cookies" element={<CookiesPage />} />
          <Route path="/indicado-por/:code" element={<InvitePage />} />
          <Route
            path="/login"
            element={<LoginPage setToken={setToken} setUser={setUser} />}
          />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route
            path="/dashboard"
            element={<SmartRedirect token={token} user={user} />}
          />

          {/* CLIENTE module */}
          <Route path="/cliente/home" element={<WithModule moduleKey="cliente" token={token} user={user} logout={logout}><ClienteHomePage user={user} logout={logout} /></WithModule>} />
          <Route path="/cliente/busca" element={<WithModule moduleKey="cliente" token={token} user={user} logout={logout}><ClienteSearchPage /></WithModule>} />
          <Route path="/cliente/restaurante/:merchantId" element={<WithModule moduleKey="cliente" token={token} user={user} logout={logout}><ClienteRestaurantPage /></WithModule>} />
          <Route path="/cliente/carrinho" element={<WithModule moduleKey="cliente" token={token} user={user} logout={logout}><ClienteCartPage /></WithModule>} />
          <Route path="/cliente/checkout" element={<WithModule moduleKey="cliente" token={token} user={user} logout={logout}><ClienteCheckoutPage user={user} /></WithModule>} />
          <Route path="/cliente/recompensas" element={<WithModule moduleKey="cliente" token={token} user={user} logout={logout}><ClienteRewardsPage user={user} /></WithModule>} />
          <Route path="/cliente/pedidos" element={<WithModule moduleKey="cliente" token={token} user={user} logout={logout}><MyOrdersPage user={user} logout={logout} /></WithModule>} />
          <Route path="/cliente/pedidos/:orderId" element={<WithModule moduleKey="cliente" token={token} user={user} logout={logout}><OrderDetailsPage user={user} logout={logout} /></WithModule>} />
          <Route path="/cliente/carteira" element={<WithModule moduleKey="cliente" token={token} user={user} logout={logout}><WalletPage user={user} logout={logout} /></WithModule>} />
          <Route path="/cliente/rede" element={<WithModule moduleKey="cliente" token={token} user={user} logout={logout}><NetworkPage user={user} logout={logout} /></WithModule>} />
          <Route path="/cliente/conversas" element={<WithModule moduleKey="cliente" token={token} user={user} logout={logout}><ChatsPage user={user} logout={logout} /></WithModule>} />
          <Route path="/cliente/conversas/:orderId" element={<WithModule moduleKey="cliente" token={token} user={user} logout={logout}><ChatsPage user={user} logout={logout} /></WithModule>} />
          <Route path="/cliente/ranking" element={<WithModule moduleKey="cliente" token={token} user={user} logout={logout}><LeaderboardPage user={user} logout={logout} /></WithModule>} />
          <Route path="/cliente/perfil" element={<WithModule moduleKey="cliente" token={token} user={user} logout={logout}><ClienteProfilePage user={user} logout={logout} /></WithModule>} />
          <Route path="/cliente/comissoes" element={<WithModule moduleKey="cliente" token={token} user={user} logout={logout}><CommissionsPage user={user} logout={logout} /></WithModule>} />
          <Route path="/cliente/favoritos" element={<WithModule moduleKey="cliente" token={token} user={user} logout={logout}><ClienteFavoritesPage user={user} /></WithModule>} />
          <Route path="/cliente/perfil/enderecos" element={<WithModule moduleKey="cliente" token={token} user={user} logout={logout}><ClienteAddressesPage user={user} /></WithModule>} />
          <Route path="/cliente/perfil/notificacoes" element={<WithModule moduleKey="cliente" token={token} user={user} logout={logout}><ClienteNotificationsPage user={user} /></WithModule>} />
          <Route path="/cliente/grupos" element={<WithModule moduleKey="cliente" token={token} user={user} logout={logout}><NossosGruposPage /></WithModule>} />
          <Route path="/cliente/virar-entregador" element={<WithModule moduleKey="cliente" token={token} user={user} logout={logout}><MigrateToDriverPage user={user} setUser={setUser} /></WithModule>} />
          <Route path="/cliente/tracking/:orderId" element={<WithModule moduleKey="cliente" token={token} user={user} logout={logout}><ClienteTrackingPage user={user} logout={logout} /></WithModule>} />

          {/* LOJA module */}
          <Route path="/loja/painel" element={<WithModule moduleKey="loja" token={token} user={user} logout={logout}><LojaDashboardPage user={user} /></WithModule>} />
          <Route path="/loja/cozinha" element={<WithModule moduleKey="loja" token={token} user={user} logout={logout}><LojaKitchenPage user={user} /></WithModule>} />
          <Route path="/loja/minha-loja" element={<WithModule moduleKey="loja" token={token} user={user} logout={logout}><LojaMinhaLojaPage user={user} /></WithModule>} />
          <Route path="/loja/painel-legacy" element={<WithModule moduleKey="loja" token={token} user={user} logout={logout}><MerchantDashboard user={user} logout={logout} /></WithModule>} />
          <Route path="/loja/pedidos" element={<WithModule moduleKey="loja" token={token} user={user} logout={logout}><LojaOrdersPage user={user} /></WithModule>} />
          <Route path="/loja/devolucoes" element={<WithModule moduleKey="loja" token={token} user={user} logout={logout}><LojaDevolucoesPage user={user} /></WithModule>} />
          <Route path="/loja/pedidos-legacy" element={<WithModule moduleKey="loja" token={token} user={user} logout={logout}><MyOrdersPage user={user} logout={logout} /></WithModule>} />
          <Route path="/loja/financeiro" element={<WithModule moduleKey="loja" token={token} user={user} logout={logout}><WalletPage user={user} logout={logout} /></WithModule>} />
          <Route path="/loja/produtos" element={<WithModule moduleKey="loja" token={token} user={user} logout={logout}><LojaProductsPage user={user} /></WithModule>} />
          <Route path="/loja/analytics" element={<WithModule moduleKey="loja" token={token} user={user} logout={logout}><LojaAnalyticsPage user={user} /></WithModule>} />
          <Route path="/loja/mapa" element={<WithModule moduleKey="loja" token={token} user={user} logout={logout}><LojaMapPage user={user} /></WithModule>} />
          <Route path="/loja/configuracoes" element={<WithModule moduleKey="loja" token={token} user={user} logout={logout}><LojaSettingsPage /></WithModule>} />
          <Route path="/loja/faturas" element={<WithModule moduleKey="loja" token={token} user={user} logout={logout}><MerchantBillingPage user={user} logout={logout} /></WithModule>} />
          <Route path="/loja/carteira" element={<WithModule moduleKey="loja" token={token} user={user} logout={logout}><WalletPage user={user} logout={logout} /></WithModule>} />
          <Route path="/loja/conversas" element={<WithModule moduleKey="loja" token={token} user={user} logout={logout}><LojaChatsPage user={user} /></WithModule>} />
          <Route path="/loja/conversas/:orderId" element={<WithModule moduleKey="loja" token={token} user={user} logout={logout}><LojaChatsPage user={user} /></WithModule>} />
          <Route path="/loja/rede" element={<WithModule moduleKey="loja" token={token} user={user} logout={logout}><NetworkPage user={user} logout={logout} /></WithModule>} />
          <Route path="/loja/perfil" element={<WithModule moduleKey="loja" token={token} user={user} logout={logout}><ProfilePage user={user} logout={logout} /></WithModule>} />
          <Route path="/loja/grupos" element={<WithModule moduleKey="loja" token={token} user={user} logout={logout}><NossosGruposPage /></WithModule>} />

          {/* ENTREGADOR module */}
          <Route path="/entregador/entregas" element={<WithModule moduleKey="entregador" token={token} user={user} logout={logout}><DriverDashboardPremium user={user} logout={logout} /></WithModule>} />
          <Route path="/entregador/entregas-legado" element={<WithModule moduleKey="entregador" token={token} user={user} logout={logout}><DriverDashboard user={user} logout={logout} /></WithModule>} />
          <Route path="/entregador/pedidos" element={<WithModule moduleKey="entregador" token={token} user={user} logout={logout}><DriverOrdersPage user={user} logout={logout} /></WithModule>} />
          <Route path="/entregador/cadastro" element={<WithModule moduleKey="entregador" token={token} user={user} logout={logout}><DriverRegistrationPage user={user} logout={logout} /></WithModule>} />
          <Route path="/entregador/mapa" element={<WithModule moduleKey="entregador" token={token} user={user} logout={logout}><DriverMapPage user={user} logout={logout} /></WithModule>} />
          <Route path="/entregador/carteira" element={<WithModule moduleKey="entregador" token={token} user={user} logout={logout}><WalletPage user={user} logout={logout} /></WithModule>} />
          <Route path="/entregador/rede" element={<WithModule moduleKey="entregador" token={token} user={user} logout={logout}><NetworkPage user={user} logout={logout} /></WithModule>} />
          <Route path="/entregador/perfil" element={
            token && user && user.user_type === "driver"
              ? <DriverProfilePremium user={user} logout={logout} />
              : <Navigate to="/login" replace />
          } />
          <Route path="/entregador/grupos" element={<WithModule moduleKey="entregador" token={token} user={user} logout={logout}><NossosGruposPage /></WithModule>} />

          {/* ADMIN module */}
          <Route path="/admin/painel" element={<AdminOnly token={token} user={user}><WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminDashboard user={user} logout={logout} /></WithModule></AdminOnly>} />
          <Route path="/admin/grupos" element={<AdminOnly token={token} user={user}><WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminGruposPage /></WithModule></AdminOnly>} />
          <Route
            path="/admin/analytics"
            element={
              token && user && (user.user_type === "admin" || user.user_type === "franchisee") ? (
                <AdminAnalyticsPage user={user} logout={logout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          {/* Legacy routes (kept for backward compatibility) */}
          <Route
            path="/network"
            element={
              <ProtectedRoute token={token}>
                <NetworkPage user={user} logout={logout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={<Navigate to="/admin/painel" replace />}
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute token={token}>
                <ProfilePage user={user} logout={logout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leaderboard"
            element={
              <ProtectedRoute token={token}>
                <LeaderboardPage user={user} logout={logout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/commissions"
            element={
              <ProtectedRoute token={token}>
                <CommissionsPage user={user} logout={logout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders"
            element={
              <ProtectedRoute token={token}>
                <MyOrdersPage user={user} logout={logout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders/:orderId"
            element={
              <ProtectedRoute token={token}>
                <OrderDetailsPage user={user} logout={logout} />
              </ProtectedRoute>
            }
          />
          <Route path="/help" element={<HelpPage user={user} logout={logout} />} />
          <Route
            path="/chats"
            element={
              <ProtectedRoute token={token}>
                <ChatsPage user={user} logout={logout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/chats/:orderId"
            element={
              <ProtectedRoute token={token}>
                <ChatsPage user={user} logout={logout} />
              </ProtectedRoute>
            }
          />
          <Route path="/pre-launch" element={<PreLaunchPage />} />
          <Route
            path="/wallet"
            element={
              <ProtectedRoute token={token}>
                <WalletPage user={user} logout={logout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/withdrawals"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminWithdrawalsPage user={user} logout={logout} /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/tracking/:orderId"
            element={
              <ProtectedRoute token={token}>
                <OrderTrackingPage user={user} logout={logout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/merchant/billing"
            element={
              <ProtectedRoute token={token}>
                <MerchantBillingPage user={user} logout={logout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/merchants/billing"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminMerchantBillingPage user={user} logout={logout} /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/admin/financial"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminFinancialDashboard user={user} logout={logout} /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/admin/payments-audit"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminPaymentsAuditPage user={user} logout={logout} /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute token={token}>
                <SettingsPage user={user} logout={logout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/coupons"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminCouponsPage user={user} logout={logout} /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/admin/emails"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminEmailsPage user={user} logout={logout} /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/admin/whatsapp"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminWhatsAppPage user={user} logout={logout} /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/admin/kyc"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminKycPage user={user} logout={logout} /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/admin/devolucoes"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminReturnsDashboardPage user={user} logout={logout} /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/admin/mobile"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminMobilePage user={user} logout={logout} /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/admin/publicidade"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminPublicidadePage user={user} logout={logout} /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/admin/chat"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminChatPage user={user} logout={logout} /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/admin/recrutamento"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminRecruitmentPage /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/admin/cnh-validations"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminCnhValidationsPage /></WithModule>
              </AdminOnly>
            }
          />
          {/* Portal do Patrocinador (Anunciante) */}
          <Route path="/anuncie" element={<AdvertiseLandingPage />} />
          <Route path="/convite/:codigo" element={<RecruitmentLandingPage />} />
          <Route path="/convite" element={<RecruitmentLandingPage />} />
          <Route path="/onboarding/:token" element={<OnboardingPage setToken={setToken} setUser={setUser} />} />
          <Route path="/patrocinador/login" element={<AdvertiserLoginPage setToken={setToken} setUser={setUser} />} />
          <Route path="/patrocinador/setup" element={<AdvertiserSetupPasswordPage />} />
          <Route
            path="/patrocinador/dashboard"
            element={
              token && user?.user_type === "advertiser"
                ? <AdvertiserDashboardPage setToken={setToken} setUser={setUser} />
                : <Navigate to="/patrocinador/login" replace />
            }
          />
          <Route
            path="/admin/settings"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminSettingsPage user={user} logout={logout} /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/admin/cities"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminCitiesPage user={user} logout={logout} /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/admin/vouchers"
            element={
              <AdminOnly token={token} user={user}>
                <AdminVouchersPage user={user} logout={logout} />
              </AdminOnly>
            }
          />
          <Route
            path="/admin/franchises"
            element={
              <AdminOnly token={token} user={user}>
                <AdminFranchisesPage user={user} logout={logout} />
              </AdminOnly>
            }
          />
          <Route
            path="/admin/users"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminUsersPage /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/admin/drivers"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminDriversPage /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/admin/merchants"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminMerchantsPage /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/admin/network"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminNetworkPage /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/admin/support"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminSupportPage /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/admin/niches"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminNichesPage /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/admin/rates"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminPlatformRatesPage /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/admin/integrations"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminIntegrationsDynamicPage user={user} logout={logout} /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/admin/integrations-legacy"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminIntegrationsPage /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/admin/banners"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminBannersPage user={user} /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/admin/fiscal"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminFiscalPage user={user} logout={logout} /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/admin/sos"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminSOSPage user={user} logout={logout} /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/admin/background-checks"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminBackgroundCheckPage user={user} logout={logout} /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/verificacao"
            element={
              token ? (
                <WithModule moduleKey={user?.user_type || "cliente"} token={token} user={user} logout={logout}>
                  <BackgroundCheckPage user={user} />
                </WithModule>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/verificacao-kyc"
            element={
              token ? (
                <WithModule moduleKey={user?.user_type || "cliente"} token={token} user={user} logout={logout}>
                  <VerificacaoKycPage user={user} />
                </WithModule>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/admin/campaigns"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminCampaignsPage user={user} logout={logout} /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/admin/hierarchy"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminHierarchyPage user={user} logout={logout} /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/admin/lojinha"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminShopPage /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/admin/seguranca"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminSafetyCenter /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/admin/auditoria"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AuditLogsPage /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/admin/perfil"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><ProfilePage user={user} logout={logout} /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/entregador/lojinha"
            element={
              token && user && (user.user_type === "driver") ? (
                <DriverShopPage user={user} logout={logout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/bolao"
            element={<BolaoPage />}
          />
          <Route
            path="/socio/convite/:token"
            element={<SocioOnboardingPage />}
          />
          <Route
            path="/socio"
            element={
              token ? <SocioDashboardPage /> : <Navigate to="/login?redirect=/socio" />
            }
          />
          <Route
            path="/admin/socios"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminSociosPage /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/admin/face-verifications"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminFaceVerificationsPage /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/admin/identity-review"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminIdentityReviewPage /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/admin/bolao"
            element={
              <AdminOnly token={token} user={user}>
                <WithModule moduleKey="admin" token={token} user={user} logout={logout}><AdminBolaoPage /></WithModule>
              </AdminOnly>
            }
          />
          <Route
            path="/suporte"
            element={
              token ? <SupportPage /> : <Navigate to="/login" />
            }
          />
          <Route
            path="/franquia/painel"
            element={
              token && user && (user.user_type === "franchisee") ? (
                <FranchiseeShell user={user} logout={logout}>
                  <FranchiseeDashboardV2 user={user} logout={logout} />
                </FranchiseeShell>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/franquia/lojas"
            element={
              token && user && (user.user_type === "franchisee") ? (
                <FranchiseeShell user={user} logout={logout}>
                  <FranchiseeStoresPage />
                </FranchiseeShell>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/franquia/pedidos"
            element={
              token && user && (user.user_type === "franchisee") ? (
                <FranchiseeShell user={user} logout={logout}>
                  <FranchiseeOrdersCenter user={user} logout={logout} />
                </FranchiseeShell>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/franquia/entregadores"
            element={
              token && user && (user.user_type === "franchisee") ? (
                <FranchiseeShell user={user} logout={logout}>
                  <FranchiseeDriversCenter user={user} logout={logout} />
                </FranchiseeShell>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/franquia/clientes"
            element={
              token && user && (user.user_type === "franchisee") ? (
                <FranchiseeShell user={user} logout={logout}>
                  <FranchiseeCustomersCenter user={user} logout={logout} />
                </FranchiseeShell>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/franquia/chat"
            element={
              token && user && (user.user_type === "franchisee") ? (
                <FranchiseeShell user={user} logout={logout}>
                  <FranchiseeChatPage user={user} logout={logout} />
                </FranchiseeShell>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          {/* ===== Pro Chat (chat profissional unificado, todas as personas) ===== */}
          <Route
            path="/franquia/chat-pro"
            element={
              token && user && (user.user_type === "franchisee") ? (
                <FranchiseeShell user={user} logout={logout}>
                  <ProChatPage user={user} />
                </FranchiseeShell>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/lojista/chat-pro"
            element={
              token && user && (user.user_type === "merchant") ? (
                <ProChatPage user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/entregador/chat"
            element={
              token && user && (user.user_type === "driver") ? (
                <WithModule moduleKey="entregador" token={token} user={user} logout={logout}>
                  <ProChatPage user={user} />
                </WithModule>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/cliente/chat"
            element={
              token && user ? (
                <ClienteShell><ProChatPage user={user} /></ClienteShell>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/franquia/financeiro"
            element={
              token && user && (user.user_type === "franchisee") ? (
                <FranchiseeShell user={user} logout={logout}>
                  <FranchiseeFinancePage />
                </FranchiseeShell>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/franquia/configuracoes"
            element={
              token && user && (user.user_type === "franchisee") ? (
                <FranchiseeShell user={user} logout={logout}>
                  <FranchiseeCitySettingsPage />
                </FranchiseeShell>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/franquia/relatorios"
            element={
              token && user && (user.user_type === "franchisee") ? (
                <FranchiseeShell user={user} logout={logout}>
                  <FranchiseeReportsPage />
                </FranchiseeShell>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/franquia/analytics"
            element={
              token && user && (user.user_type === "franchisee") ? (
                <FranchiseeShell user={user} logout={logout}>
                  <FranchiseeAnalyticsPage />
                </FranchiseeShell>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/franquia/rankings"
            element={
              token && user && (user.user_type === "franchisee") ? (
                <FranchiseeShell user={user} logout={logout}>
                  <FranchiseeRankingsPage />
                </FranchiseeShell>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/franquia/perfil"
            element={
              token && user && (user.user_type === "franchisee") ? (
                <FranchiseeShell user={user} logout={logout}>
                  <FranchiseeProfilePage />
                </FranchiseeShell>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/franquia/comunicados"
            element={
              token && user && (user.user_type === "franchisee") ? (
                <FranchiseeShell user={user} logout={logout}>
                  <FranchiseeBroadcastsPage user={user} logout={logout} />
                </FranchiseeShell>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/franquia/auditoria"
            element={
              token && user && (user.user_type === "franchisee") ? (
                <FranchiseeShell user={user} logout={logout}>
                  <AuditLogsPage />
                </FranchiseeShell>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/lojista/chat"
            element={
              token && user && (user.user_type === "merchant") ? (
                <MerchantChatPage user={user} logout={logout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/loja/franquia"
            element={
              token && user && (user.user_type === "merchant") ? (
                <WithModule moduleKey="loja" token={token} user={user} logout={logout}>
                  <MerchantChatPage user={user} logout={logout} />
                </WithModule>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/franquia/legado"
            element={
              token && user && (user.user_type === "franchisee") ? (
                <FranchiseeDashboard user={user} logout={logout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/master/dashboard"
            element={
              token && user && (user.user_type === "master_franchise_admin") ? (
                <MasterFranchiseDashboard user={user} logout={logout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/franquia-regional/dashboard"
            element={
              token && user && (user.user_type === "franchise_admin") ? (
                <FranchiseAdminDashboard user={user} logout={logout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
        </Suspense>
        </CartProvider>
      </BrowserRouter>
      <Toaster position="top-right" />
      <InstallAppPrompt />
      <OfflineQueueBadge />

      {/* Force password change on first login */}
      <ForcePasswordChangeModal
        open={!!(token && user && user.must_change_password)}
        onSuccess={() => {
          // Refetch user so must_change_password becomes false
          setUser((prev) => (prev ? { ...prev, must_change_password: false } : prev));
        }}
      />
    </div>
  );
}

export default App;
